import React, { useMemo, useState } from 'react'
import { useAppSelector } from "../../../hooks/useAppSelector"
import { ResultsGridDataCol } from './ResultsGridColData'
import { ResultsGridViewCol } from './ResultsGridColView'

import './ResultGrid.css'
import { useDispatch } from 'react-redux'
import { searchDataThunk } from '../../../redux/slices'
import { useTimezone } from '../../../hooks/useTimezone'
import RenderDataModal from '../Modals/RenderViewModal/RenderViewModal'
import {
    TanStackDatagrid,
    TanStackDatagridWithExport,
} from '../../../components'
import { DataSearchResponseItem } from '../../../services/endpoints/data'
import {
    TanStackDatagridColumn,
    TanStackDatagridFilter,
    TanStackDatagridGrouping,
    TanStackDatagridPagination,
    TanStackDatagridSort
} from '../../../components/TanStackDataGrid/TanStackDatagrid'

export const ResultsGrid: React.FC = () => {
    const data = useAppSelector(state => state.data.data)
    const prevOrder = useAppSelector(state => state.data.lastRequest?.order)
    const prevSort = useAppSelector(state => state.data.lastRequest?.sort)
    const pager = useAppSelector(state => state.data.paging)
    const groupList = useAppSelector(state => state.data.groups)
    const { formatLocalTimeFromUtc } = useTimezone()
    const dispatch = useDispatch()
    const [filters, setFilters] = useState<TanStackDatagridFilter[]>([])
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({ groupId: false })
    const [grouping, setGrouping] = useState<TanStackDatagridGrouping>(['groupId'])
    const [groupingEnabled, setGroupingEnabled] = useState(true)


    if(data === null) {
        return <></>
    }

    const sort = useMemo<TanStackDatagridSort | null>(() => {
        if (!prevSort || !prevOrder) {
            return null
        }

        return {
            columnId: prevSort,
            direction: prevOrder === 'desc' ? 'desc' : 'asc'
        }
    }, [prevOrder, prevSort])

    const handleSortChange = (nextSort: TanStackDatagridSort | null) => {
        if (!nextSort) {
            return
        }

        dispatch(
            searchDataThunk({
                sort: nextSort.columnId,
                order: nextSort.direction
            })
        )
    }

    const pagination = useMemo<TanStackDatagridPagination | undefined>(() => {
        if (!pager) {
            return undefined
        }

        return {
            pageIndex: Math.floor(pager.offset / pager.max),
            pageSize: pager.max,
            totalRows: pager.total
        }
    }, [pager])

    const handlePaginationChange = (nextPagination: TanStackDatagridPagination) => {
        if (!pager) {
            return
        }

        const nextOffset = nextPagination.pageIndex * nextPagination.pageSize

        if (nextOffset === pager.offset && nextPagination.pageSize === pager.max) {
            return
        }

        dispatch(
            searchDataThunk({
                offset: nextOffset,
                max: nextPagination.pageSize
            })
        )
    }

    const renderTimeCol = (d: Partial<{value: string}>) => {
        return formatLocalTimeFromUtc(d.value || null)
    }

    const columns = useMemo<TanStackDatagridColumn<DataSearchResponseItem>[]>(() => {
        return [
            {
                id: 'groupId',
                header: 'Group',
                accessor: (row) => {
                    const key = String(row.groupId ?? '')
                    const groupIndex = Number(key)
                    const groupName = Number.isFinite(groupIndex)
                        ? groupList[groupIndex]
                        : undefined

                    return `Group ID: ${groupName ?? key}`
                },
                groupable: true,
                groupCell: (value, groupedRows) => {
                    const collections = groupedRows.reduce<Record<string, number>>((acc, groupedRow) => {
                        const collectionName = groupedRow.collection || 'Unknown Collection'
                        acc[collectionName] = (acc[collectionName] || 0) + 1
                        return acc
                    }, {})

                    const collectionLabel = Object.entries(collections)
                        .map(([collectionName, count]) => `${collectionName} (${count})`)
                        .join(' ')

                    return collectionLabel ? `${String(value)} - ${collectionLabel}` : value
                },
                resizable: true,
                hideable: true
            },
            {
                id: 'startDate',
                header: 'Start',
                accessor: (row) => row.startDate || '',
                cell: (row) => renderTimeCol({ value: row.startDate }),
                sortable: true,
                resizable: true,
                hideable: true
            },
            {
                id: 'endDate',
                header: 'End',
                accessor: (row) => row.endDate || '',
                cell: (row) => renderTimeCol({ value: row.endDate }),
                sortable: true,
                resizable: true,
                hideable: true
            },
            {
                id: 'vehicle.displayName',
                accessor: (row) => row.vehicle?.displayName || '',
                header: 'Vehicle',
                sortable: true,
                resizable: true,
                hideable: true
            },
            {
                id: 'displayName',
                accessor: (row) => row.displayName || '',
                header: 'Name',
                sortable: true,
                resizable: true,
                hideable: true
            },
            {
                id: 'uploadId',
                accessor: (row) => row.uploadId || '',
                header: 'Upload Id',
                sortable: true,
                resizable: true,
                hideable: true
            },
            {
                id: 'view',
                header: 'View',
                accessor: (row) => row.versions,
                className: 'p-0 results-view-cell',
                cell: (row) => <ResultsGridViewCol item={row} />,
                resizable: true,
                hideable: true
            },
            {
                id: 'dataType',
                header: 'Data Type',
                accessor: (row) => row.versions,
                className: 'p-0 results-data-type-cell',
                cell: (row) => <ResultsGridDataCol item={row} />,
                resizable: true,
                hideable: true
            },
        ]
    }, [groupList, formatLocalTimeFromUtc])

    return (
        <>
        <div className="dataset-display" id='datasetGroup'>
            <div id="dataGridParent">
                <TanStackDatagridWithExport
                    data={data}
                    columns={columns}
                    getRowId={(row) => String(row.id)}
                    grouping={groupingEnabled ? grouping : undefined}
                    onGroupingChange={setGrouping}
                    sort={sort}
                    onSortChange={handleSortChange}
                    filters={filters}
                    onFilterChange={setFilters}
                    enablePagination={Boolean(pagination)}
                    pagination={pagination}
                    onPaginationChange={handlePaginationChange}
                    enableColumnSelection={false}
                    columnVisibility={{
                        ...columnVisibility,
                        groupId: groupingEnabled ? false : true
                    }}
                    onColumnVisibilityChange={setColumnVisibility}
                />
            </div>
        </div>
        {pager && <MaxCollections opener={pager.max * 200 <= data.length} />}
        </>
    )
}


const MaxCollections: React.FC<{opener:boolean}> = ({opener}) => {
    const [showDialog, setShowDialog] = useState(opener)

    return (
        <>
            {showDialog && <MaxCollectionsDialog onClose={()=> setShowDialog(false)} />}
        </>
    )
}

interface maxCollectionsDialogProps {
    onClose: () => void
  }
const MaxCollectionsDialog: React.FC<maxCollectionsDialogProps> = ({ onClose }) => {
return (
    <RenderDataModal onClose={onClose} title='Too many Collections' className='settings-modal'>
        { <span className="export-alert alert-warning">
            Collection limit reached. Some collections may be cut off.
        </span>}
    </RenderDataModal>
)
}
